import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import {
  canOpenDokumentZahlungen,
  dokumentZahlungButtonLabel,
  getDokumentListStatusBadge,
  type DokumentListItem,
} from "@/lib/dokumente";
import { Button } from "@/components/ui/Button";
import { EhDataTableShell, SortableHeader, type SortState } from "@/components/dataTable";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import {
  clampDocumentColumnWidth,
  getDocumentColumnDefaultWidth,
  getDocumentColumnLabel,
  renderDocumentCellValue,
  zahlungErfasstLines,
  zahlungProtokollLines,
  type DocumentColumnKey,
} from "@/pages/dokumente/documentColumns";
const ACTIONS_COL_WIDTH = 7.5;

function paymentAuditLines(row: DokumentListItem, columns: DocumentColumnKey[]): string[] {
  if (columns.includes("zahlung_erfasst")) return [];
  const protocol = zahlungProtokollLines(row.zahlung_protokoll, row.waehrung);
  const lines = protocol.length
    ? protocol
    : zahlungErfasstLines(row.zahlung_eintraege, row.waehrung);
  return lines.slice(0, 3).map((line) => line.text);
}

function PaymentAuditLines({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {lines.map((line, index) => (
        <div key={`${index}-${line}`} className="text-[11px] leading-snug text-label-secondary">
          {line}
        </div>
      ))}
    </div>
  );
}

export interface DocumentTableProps {
  rows: DokumentListItem[];
  columns: DocumentColumnKey[];
  widths: Record<string, number>;
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onRowOpen: (row: DokumentListItem) => void;
  onCapturePayment?: (row: DokumentListItem) => void;
  paymentBusyId?: number | null;
  onColumnWidthChange?: (key: string, width: number) => void;
  emptyText?: string;
  sort?: SortState | null;
  onSort?: (key: string) => void;
}

export function DocumentTable({
  rows,
  columns,
  widths,
  loading,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onRowOpen,
  onCapturePayment,
  paymentBusyId = null,
  onColumnWidthChange,
  emptyText = "Keine Dokumente gefunden.",
  sort,
  onSort,
}: DocumentTableProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore || !onLoadMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting && !loading && !loadingMore) {
          onLoadMore();
        }
      },
      { rootMargin: "240px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore]);

  return (
    <div>
      <div className="hidden md:block">
        <DesktopTable
          rows={rows}
          columns={columns}
          widths={widths}
          onRowOpen={onRowOpen}
          onCapturePayment={onCapturePayment}
          paymentBusyId={paymentBusyId}
          onColumnWidthChange={onColumnWidthChange}
          loading={loading}
          emptyText={emptyText}
          sort={sort}
          onSort={onSort}
        />
      </div>
      <div className="md:hidden">
        <MobileCards
          rows={rows}
          columns={columns}
          onRowOpen={onRowOpen}
          onCapturePayment={onCapturePayment}
          paymentBusyId={paymentBusyId}
          loading={loading}
          emptyText={emptyText}
        />
      </div>
      {hasMore ? (
        <div ref={sentinelRef} className="flex justify-center py-4 text-sm text-label-tertiary">
          {loadingMore ? "Lade weitere …" : "Weitere Dokumente laden …"}
        </div>
      ) : null}
      {!hasMore && !loading && rows.length > 0 ? (
        <div className="py-4 text-center text-xs text-label-tertiary">
          Ende der Liste · {rows.length} {rows.length === 1 ? "Dokument" : "Dokumente"}
        </div>
      ) : null}
    </div>
  );
}

interface DesktopTableProps {
  rows: DokumentListItem[];
  columns: DocumentColumnKey[];
  widths: Record<string, number>;
  onRowOpen: (row: DokumentListItem) => void;
  onCapturePayment?: (row: DokumentListItem) => void;
  paymentBusyId: number | null;
  onColumnWidthChange?: (key: string, width: number) => void;
  loading: boolean;
  emptyText: string;
  sort?: SortState | null;
  onSort?: (key: string) => void;
}

function DesktopThead({
  columns,
  showActions,
  activeResize,
  onResizePointerDown,
  sort,
  onSort,
}: {
  columns: DocumentColumnKey[];
  showActions: boolean;
  activeResize: string | null;
  onResizePointerDown: (e: React.PointerEvent, colKey: string, idx: number) => void;
  sort?: SortState | null;
  onSort?: (key: string) => void;
}) {
  return (
    <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
      <tr>
        {columns.map((col, idx) => (
          <th
            key={col}
            scope="col"
            className="eh-data-th whitespace-normal break-words px-3 py-2 text-left font-semibold leading-snug"
          >
            {[
              "dokument_nummer",
              "dokument_typ",
              "status",
              "kunde",
              "belegdatum",
              "created_at",
              "betreff",
              "summe_brutto",
              "summe_netto",
            ].includes(col) && onSort ? (
              <SortableHeader
                label={getDocumentColumnLabel(col)}
                columnKey={col}
                sort={sort ?? null}
                onSort={onSort}
              />
            ) : (
              <span className="block" title={getDocumentColumnLabel(col)}>
                {getDocumentColumnLabel(col)}
              </span>
            )}
            <span
              className={clsx("eh-data-resizer", activeResize === col && "is-active")}
              role="separator"
              aria-orientation="vertical"
              title="Spaltenbreite ziehen"
              onPointerDown={(e) => onResizePointerDown(e, col, idx)}
            />
          </th>
        ))}
        {showActions ? (
          <th
            scope="col"
            className="eh-data-th whitespace-nowrap px-3 py-2 text-right font-semibold leading-snug"
          >
            <span className="sr-only">Aktionen</span>
          </th>
        ) : null}
      </tr>
    </thead>
  );
}

function DesktopTable({
  rows,
  columns,
  widths,
  onRowOpen,
  onCapturePayment,
  paymentBusyId,
  onColumnWidthChange,
  loading,
  emptyText,
  sort,
  onSort,
}: DesktopTableProps) {
  const showActions = Boolean(onCapturePayment);
  const dataWidths = useMemo(
    () =>
      columns.map((col) => {
        const stored = Number(widths?.[col]);
        return Number.isFinite(stored) && stored > 0
          ? clampDocumentColumnWidth(stored)
          : getDocumentColumnDefaultWidth(col);
      }),
    [columns, widths],
  );

  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const floatingColRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const [activeResize, setActiveResize] = useState<string | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const floatingSyncKey = useMemo(
    () =>
      [columns.join("|"), dataWidths.join(","), rows.length, showActions ? "actions" : ""].join("§"),
    [columns, dataWidths, rows.length, showActions],
  );

  const floating = useFloatingProjectTableHeader(tableWrapRef, tableRef, {
    enabled: columns.length > 0,
    syncKey: floatingSyncKey,
  });

  function startResize(event: React.PointerEvent, colKey: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    setActiveResize(colKey);

    const startX = event.clientX;
    const startWidth = dataWidths[idx] ?? getDocumentColumnDefaultWidth(colKey);
    const colEl = colRefs.current[colKey];
    let lastWidth = startWidth;

    function applyWidth(width: number) {
      lastWidth = clampDocumentColumnWidth(width);
      if (colEl) colEl.style.width = `${lastWidth}px`;
      const floatCol = floatingColRefs.current[colKey];
      if (floatCol) floatCol.style.width = `${lastWidth}px`;
    }

    function onMove(e: PointerEvent) {
      applyWidth(startWidth + (e.clientX - startX));
    }

    function onUp() {
      target.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setActiveResize(null);
      if (lastWidth !== startWidth) onColumnWidthChange?.(colKey, lastWidth);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  const colSpan = columns.length + (showActions ? 1 : 0);

  return (
    <>
      <EhDataTableShell ref={tableWrapRef}>
        <table ref={tableRef} className="eh-data-table divide-y divide-separator text-sm">
          <colgroup>
            {columns.map((col, idx) => (
              <col
                key={col}
                ref={(el) => {
                  colRefs.current[col] = el;
                }}
                style={{ width: `${dataWidths[idx]}px` }}
              />
            ))}
            {showActions ? <col style={{ width: `${ACTIONS_COL_WIDTH}rem` }} /> : null}
          </colgroup>

          <DesktopThead
            columns={columns}
            showActions={showActions}
            activeResize={activeResize}
            onResizePointerDown={startResize}
            sort={sort}
            onSort={onSort}
          />

          <tbody className="divide-y divide-separator bg-card">
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={colSpan} className="px-3 py-8 text-center text-sm text-label-tertiary">
                  {emptyText}
                </td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr
                key={`${row.quelle || "geschaeftsdokument"}-${row.hero_document_id ?? row.timesheet_id ?? row.stueckliste_id ?? row.id}`}
                onClick={() => onRowOpen(row)}
                className="eh-data-row cursor-pointer align-middle transition"
              >
                {columns.map((col) => {
                  const cell = renderDocumentCellValue(row, col);
                  const auditLines = col === "status" ? paymentAuditLines(row, columns) : [];
                  return (
                    <td
                      key={col}
                      className={clsx(
                        "eh-data-body-cell min-w-0 align-middle whitespace-normal break-words px-3 py-2 text-label",
                        cell.mono && "font-mono tabular-nums",
                        cell.alignRight && "text-right",
                        col === "status" && "text-label-secondary",
                      )}
                      title={cell.title || cell.value || undefined}
                    >
                      {cell.kind === "status" ? (
                        <div>
                          <span
                            className={clsx(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                              cell.badgeClass || getDokumentListStatusBadge(row),
                            )}
                          >
                            {cell.value}
                          </span>
                          <PaymentAuditLines lines={auditLines} />
                        </div>
                      ) : cell.detailLines?.length ? (
                        <div className="space-y-0.5">
                          {cell.detailLines.map((line, index) => (
                            <div key={`${index}-${line}`} className="leading-snug">
                              {line}
                            </div>
                          ))}
                        </div>
                      ) : col === "dokument_nummer" ? (
                        <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                          <span
                            className={clsx(
                              "min-w-0 break-words",
                              cell.mono && "font-mono tabular-nums",
                              cell.valueClass,
                            )}
                          >
                            {cell.value || "—"}
                          </span>
                          {cell.source === "hero" ? (
                            <span
                              className="shrink-0 rounded-full bg-fill-tertiary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-label-secondary"
                              title="Aus Hero"
                            >
                              Hero
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className={clsx("block w-full break-words", cell.valueClass)}>
                          {cell.value || "—"}
                        </span>
                      )}
                    </td>
                  );
                })}
                {showActions ? (
                  <td
                    className="eh-data-body-cell whitespace-nowrap px-3 py-2 text-right align-middle"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {canOpenDokumentZahlungen(row) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        loading={paymentBusyId === row.id}
                        disabled={paymentBusyId != null}
                        onClick={() => onCapturePayment?.(row)}
                      >
                        {dokumentZahlungButtonLabel(row)}
                      </Button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </EhDataTableShell>

      {floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{ top: floating.top, left: floating.left, width: floating.width }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                <colgroup>
                  {columns.map((col, idx) => (
                    <col
                      key={col}
                      ref={(el) => {
                        floatingColRefs.current[col] = el;
                      }}
                      style={{ width: `${dataWidths[idx]}px` }}
                    />
                  ))}
                  {showActions ? <col style={{ width: `${ACTIONS_COL_WIDTH}rem` }} /> : null}
                </colgroup>
                <DesktopThead
                  columns={columns}
                  showActions={showActions}
                  activeResize={activeResize}
                  onResizePointerDown={startResize}
                  sort={sort}
                  onSort={onSort}
                />
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function MobileCards({
  rows,
  columns,
  onRowOpen,
  onCapturePayment,
  paymentBusyId,
  loading,
  emptyText,
}: {
  rows: DokumentListItem[];
  columns: DocumentColumnKey[];
  onRowOpen: (row: DokumentListItem) => void;
  onCapturePayment?: (row: DokumentListItem) => void;
  paymentBusyId: number | null;
  loading: boolean;
  emptyText: string;
}) {
  if (rows.length === 0 && !loading) {
    return (
      <div className="rounded-ios border border-dashed border-separator px-4 py-8 text-center text-sm text-label-secondary">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const primary = renderDocumentCellValue(row, "dokument_nummer");
        const status = renderDocumentCellValue(row, "status");
        const showPayment = Boolean(onCapturePayment) && canOpenDokumentZahlungen(row);
        return (
          <div
            key={`${row.quelle || "geschaeftsdokument"}-${row.hero_document_id ?? row.timesheet_id ?? row.stueckliste_id ?? row.id}`}
            className="w-full rounded-ios border border-separator bg-card px-4 py-3 text-left shadow-ios transition hover:bg-fill-tertiary/40"
          >
            <button type="button" onClick={() => onRowOpen(row)} className="w-full text-left">
              <div className="flex items-start justify-between gap-3">
                <span className="inline-flex min-w-0 items-center gap-1.5 font-semibold text-label">
                  <span className="min-w-0 break-words">{primary.value || "Entwurf"}</span>
                  {primary.source === "hero" ? (
                    <span
                      className="shrink-0 rounded-full bg-fill-tertiary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-label-secondary"
                      title="Aus Hero"
                    >
                      Hero
                    </span>
                  ) : null}
                </span>
                <div className="shrink-0 text-right">
                  <span
                    className={clsx(
                      "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                      status.badgeClass || getDokumentListStatusBadge(row),
                    )}
                  >
                    {status.value}
                  </span>
                  <PaymentAuditLines lines={paymentAuditLines(row, columns)} />
                </div>
              </div>
              <dl className="mt-2 grid gap-1 text-sm">
                {columns
                  .filter((col) => col !== "dokument_nummer" && col !== "status")
                  .map((col) => {
                    const cell = renderDocumentCellValue(row, col);
                    return (
                      <div key={col} className={clsx("flex gap-2", col === "zahlung_erfasst" && "flex-col gap-0.5")}>
                        <dt className={clsx("shrink-0 text-label-tertiary", col === "zahlung_erfasst" ? "w-full" : "w-24")}>
                          {getDocumentColumnLabel(col)}
                        </dt>
                        <dd className={clsx("min-w-0 break-words text-label", cell.valueClass)}>
                          {cell.detailLines?.length
                            ? cell.detailLines.map((line, index) => (
                                <div key={`${index}-${line}`}>{line}</div>
                              ))
                            : cell.value || "—"}
                        </dd>
                      </div>
                    );
                  })}
              </dl>
            </button>
            {showPayment ? (
              <div className="mt-3 flex justify-end border-t border-separator pt-3">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  loading={paymentBusyId === row.id}
                  disabled={paymentBusyId != null}
                  onClick={() => onCapturePayment?.(row)}
                >
                  {dokumentZahlungButtonLabel(row, { mobile: true })}
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
